'use client';

import { useState, useEffect, useRef } from 'react';
import { Menu, X } from 'lucide-react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { reportGoogleAdsConversion } from '../_lib/google-ads';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentHash, setCurrentHash] = useState('');
  const [mounted, setMounted] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const isUserNavigatingRef = useRef(false);
  const pathname = usePathname();

  useEffect(() => {
    // 다음 틱에서 mounted를 리셋하고 다시 설정
    const timer = setTimeout(() => {
      setMounted(false);
      setTimeout(() => {
        setMounted(true);
        setCurrentHash(window.location.hash);
      }, 0);
    }, 0);

    const handleHashChange = () => {
      setCurrentHash(window.location.hash);
    };

    window.addEventListener('hashchange', handleHashChange);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [pathname]);

  useEffect(() => {
    if (!mounted) return;
    // /pricing 페이지는 검색 전용 단일 페이지 — 해시 동기화 안 함 (URL에 #pricing 붙지 않게)
    if (pathname === '/pricing') return;

    // Intersection Observer로 섹션 가시성 감지
    const sections = ['about', 'features', 'blog', 'pricing', 'faq'];
    const sectionElements = sections.map(id => document.getElementById(id)).filter(Boolean) as HTMLElement[];

    if (sectionElements.length === 0) return;

    const updateHash = (hash: string, force = false) => {
      // 사용자가 링크를 클릭한 경우 일정 시간 동안 자동 업데이트 비활성화
      if (isUserNavigatingRef.current && !force) return;

      if (window.location.hash !== hash) {
        // replaceState를 사용하여 히스토리에 추가하지 않고 URL만 업데이트
        // 빈 문자열일 경우 pathname만 사용하여 해시 제거
        const newUrl = hash || window.location.pathname;
        window.history.replaceState(null, '', newUrl);
        setCurrentHash(hash);
      }
    };

    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -60% 0px', // 뷰포트 상단 20% 지점에서 활성화
      threshold: 0,
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      // 현재 뷰포트에 보이는 섹션들 찾기
      const visibleSections = entries.filter(entry => entry.isIntersecting).map(entry => entry.target.id);

      if (visibleSections.length > 0) {
        // 가장 위에 있는 섹션 선택
        const sortedSections = sectionElements
          .filter(el => visibleSections.includes(el.id))
          .sort((a, b) => {
            const rectA = a.getBoundingClientRect();
            const rectB = b.getBoundingClientRect();
            return rectA.top - rectB.top;
          });

        if (sortedSections.length > 0) {
          const sectionId = sortedSections[0].id;
          // FAQ 섹션은 헤더 링크가 없으므로 해시 제거
          if (sectionId === 'faq') {
            setActiveSection('');
            updateHash('', true);
          } else {
            const newHash = `#${sectionId}`;
            setActiveSection(newHash);
            updateHash(newHash);
          }
        }
      } else {
        // 모든 섹션을 벗어난 경우
        setActiveSection('');
        // 페이지 최상단이거나 마지막 섹션을 지나친 경우 해시 제거
        updateHash('', true);
      }
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    sectionElements.forEach(section => {
      observer.observe(section);
    });

    // 스크롤 이벤트로도 확인 (Intersection Observer가 놓칠 수 있는 경우 대비)
    const handleScroll = () => {
      if (isUserNavigatingRef.current) return; // 사용자 네비게이션 중에는 스크롤 감지 비활성화

      const scrollPosition = window.scrollY + 200; // 헤더 높이 고려
      let foundSection = false;

      // 모든 섹션의 범위 확인
      for (let i = sectionElements.length - 1; i >= 0; i--) {
        const section = sectionElements[i];
        const sectionTop = section.offsetTop;
        const sectionHeight = section.offsetHeight;

        if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
          // FAQ 섹션은 헤더 링크가 없으므로 해시 제거
          if (section.id === 'faq') {
            setActiveSection('');
            updateHash('', true);
          } else {
            const newHash = `#${section.id}`;
            setActiveSection(newHash);
            updateHash(newHash);
          }
          foundSection = true;
          break;
        }
      }

      // 모든 섹션을 벗어난 경우
      if (!foundSection) {
        setActiveSection('');
        // 모든 섹션을 벗어난 경우 해시 제거 (상단 또는 하단)
        updateHash('', true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // 초기 실행

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, [mounted, pathname]);

  const navLinks = [
    { label: 'About Us', href: '#about' },
    { label: 'Features', href: '#features' },
    { label: 'Blog', href: '#blog' },
    { label: 'Pricing', href: '#pricing' },
  ];

  return (
    <nav className='fixed top-0 left-0 right-0 z-50 flex items-center justify-center py-4'>
      <div
        className='relative flex items-center gap-0 rounded-full px-5 py-2 pr-2 overflow-visible'
        style={{
          background: '#1a1a20',
        }}
      >
        {/* Gradient border */}
        <div
          className='absolute inset-[-2px] rounded-full opacity-50 hover:opacity-80 transition-opacity -z-10'
          style={{
            background: 'linear-gradient(135deg, #7c3aed, #a855f7, #22c55e, #00e676)',
          }}
        />
        <div className='absolute inset-0 rounded-full -z-5' style={{ background: '#1a1a20' }} />

        {/* Logo */}
        <Link href='/' className='flex items-center gap-2.5 mr-8'>
          <div className='w-8 h-8 rounded-[9px] overflow-hidden shrink-0'>
            <Image
              src={`${process.env.NEXT_PUBLIC_CDN_BASE_URL}/homepage/tindlo-icon.png`}
              alt='Tindlo'
              className='w-full h-full object-cover'
              width={32}
              height={32}
            />
          </div>
        </Link>

        {/* Desktop Nav Links */}
        <div className='hidden md:flex items-center gap-7 mr-5'>
          {navLinks.map(link => {
            // URL 해시를 우선순위로 하이라이팅
            // blog, pricing 링크는 해당 pathname일 때도 활성화
            const isBlogLink = link.href === '#blog';
            const isPricingLink = link.href === '#pricing';
            const isActive =
              mounted &&
              link.href.startsWith('#') &&
              (currentHash === link.href ||
                (!currentHash && activeSection === link.href) ||
                (isBlogLink && pathname === '/blog') ||
                (isPricingLink && pathname === '/pricing'));
            const linkClassName = `text-sm font-medium transition-colors ${
              isActive ? 'text-[#00ff85]' : 'text-white/55 hover:text-white'
            }`;

            const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
              if (link.href.startsWith('#')) {
                e.preventDefault();

                // pathname이 있고 홈페이지가 아닌 경우 홈페이지로 이동
                if (pathname && pathname !== '/') {
                  // 홈페이지로 이동하고 해시 추가
                  window.location.href = `/${link.href}`;
                } else {
                  // 사용자 네비게이션 시작
                  isUserNavigatingRef.current = true;

                  // 즉시 URL 업데이트
                  window.history.pushState(null, '', link.href);
                  setCurrentHash(link.href);
                  setActiveSection(link.href);

                  // 해당 섹션으로 스크롤
                  const targetId = link.href.substring(1);
                  const targetElement = document.getElementById(targetId);
                  if (targetElement) {
                    targetElement.scrollIntoView({ behavior: 'smooth' });
                  }

                  // 스크롤이 완료된 후 자동 감지 재개 (1초 후)
                  setTimeout(() => {
                    isUserNavigatingRef.current = false;
                  }, 1000);
                }
              }
            };

            return (
              <a
                key={link.label}
                href={pathname && pathname !== '/' ? `/${link.href}` : link.href}
                className={linkClassName}
                onClick={handleLinkClick}
                suppressHydrationWarning
              >
                {link.label}
              </a>
            );
          })}
        </div>

        {/* CTA Button */}
        <Link
          href='https://app.tindlo.com/'
          target='_blank'
          onClick={() => reportGoogleAdsConversion()}
          className='hidden md:inline-flex items-center px-6 py-2.5 rounded-full text-sm font-bold text-white transition-all duration-300 navbar-cta-breathe'
          style={{
            background: 'linear-gradient(135deg, #7c3aed, #22c55e)',
          }}
        >
          Get Started
        </Link>

        <style>{`
          @keyframes ctaBreathe {
            0%, 100% {
              box-shadow: 0 0 12px 2px rgba(124,58,237,.25), 0 0 30px 4px rgba(34,197,94,.1);
              filter: brightness(1);
            }
            50% {
              box-shadow: 0 0 24px 6px rgba(124,58,237,.45), 0 0 60px 10px rgba(34,197,94,.2), 0 0 80px 14px rgba(255,100,30,.1);
              filter: brightness(1.15);
            }
          }
          .navbar-cta-breathe {
            animation: ctaBreathe 3s ease-in-out infinite;
          }
          .navbar-cta-breathe:hover {
            background: linear-gradient(135deg, #7c3aed, #22c55e) !important;
            filter: brightness(1.07);
            box-shadow:
              0 0 18px 4px rgba(124, 58, 237, 0.32),
              0 0 44px 10px rgba(34, 197, 94, 0.14) !important;
            transform: scale(1.02);
            animation: none !important;
          }
        `}</style>

        {/* Mobile Menu Button */}
        <button className='md:hidden text-white p-2' onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className='absolute top-full mt-2 left-4 right-4 md:hidden rounded-2xl border border-border bg-white p-4 shadow-lg'>
          <div className='flex flex-col gap-3'>
            {navLinks.map(link => {
              // URL 해시를 우선순위로 하이라이팅
              // blog 링크는 pathname이 /blog일 때도 활성화
              const isBlogLink = link.href === '#blog';
              const isActive =
                mounted &&
                link.href.startsWith('#') &&
                (currentHash === link.href ||
                  (!currentHash && activeSection === link.href) ||
                  (isBlogLink && pathname === '/blog'));
              const linkClassName = `transition-colors py-1 ${
                isActive ? 'text-[#00ff85]' : 'text-foreground hover:text-primary'
              }`;

              const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
                if (link.href.startsWith('#')) {
                  e.preventDefault();

                  // pathname이 있고 홈페이지가 아닌 경우 홈페이지로 이동
                  if (pathname && pathname !== '/') {
                    // 홈페이지로 이동하고 해시 추가
                    window.location.href = `/${link.href}`;
                  } else {
                    // 사용자 네비게이션 시작
                    isUserNavigatingRef.current = true;

                    // 즉시 URL 업데이트
                    window.history.pushState(null, '', link.href);
                    setCurrentHash(link.href);
                    setActiveSection(link.href);

                    // 해당 섹션으로 스크롤
                    const targetId = link.href.substring(1);
                    const targetElement = document.getElementById(targetId);
                    if (targetElement) {
                      targetElement.scrollIntoView({ behavior: 'smooth' });
                    }

                    // 스크롤이 완료된 후 자동 감지 재개 (1초 후)
                    setTimeout(() => {
                      isUserNavigatingRef.current = false;
                    }, 1000);
                  }
                }
                setIsOpen(false);
              };

              return (
                <a
                  key={link.label}
                  href={pathname && pathname !== '/' ? `/${link.href}` : link.href}
                  className={linkClassName}
                  onClick={handleLinkClick}
                  suppressHydrationWarning
                >
                  {link.label}
                </a>
              );
            })}
            <Link
              href='https://app.tindlo.com/'
              target='_blank'
              className='mt-2 text-center px-6 py-3 rounded-full text-sm font-bold text-white'
              style={{ background: 'linear-gradient(135deg, #7c3aed, #22c55e)' }}
              onClick={() => {
                reportGoogleAdsConversion();
                setIsOpen(false);
              }}
            >
              Get Started
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
